import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { Button } from '@efexteam/components';
import authSvc from '../../services/auth/authSvc';
import mixpanelSvc from '../../services/mixpanel/mixpanelSvc';
import { aesEncryptCBC } from '../../utils/crypt';
import { selectAuthState } from '../login/auth/selectors';
import RegisterForm from './form/RegisterForm';
import { FormProvider } from './form/context';
import LangSelector from '@/components/navbar/lang';
import useWhiteLabelService from '../login/hooks/useWhiteLabel.service';
import LogoController from '../login/components/LogoController/LogoController';

const RegisterLayout = () => {
  const { whiteLabelState, getWhiteLabelFromLocalStorage, getWhiteLabel } =
    useWhiteLabelService();
  const navigate = useNavigate();
  const [errorMessageData, setErrorMessageData] = useState('');
  const [errorCode, setErrorCode] = useState<string | undefined>(undefined);
  const [searchParams] = useSearchParams();
  const authState = useSelector(selectAuthState);
  const [showLoader, setShowLoader] = useState(false);
  const params = Object.fromEntries(searchParams);
  const { t } = useTranslation();
  const location = useLocation();

  const isSubAccount = useMemo(
    () => location.pathname.includes('register-sub-account'),
    [location]
  );

  useEffect(() => {
    if (authState.token) {
      navigate('/dashboard');
    }
  }, [authState, navigate]);

  const createUser = async (valueInsert: any) => {
    const { entity, referral, phone, referralId, ...FixedUser } = valueInsert;
    FixedUser.countryOrigin = valueInsert.country;
    FixedUser.receivable_uuid = searchParams.get('uuid');
    FixedUser.userType = valueInsert.entity.toUpperCase();
    FixedUser.firstName = valueInsert.firstName;
    FixedUser.lastName = valueInsert.lastName;
    FixedUser.email = valueInsert.email.toLowerCase();
    FixedUser.referrerId = valueInsert.referralId;
    FixedUser.phone_number = valueInsert.phone;
    FixedUser.password = aesEncryptCBC(FixedUser.password);
    setShowLoader(true);
    setErrorCode(undefined);
    setErrorMessageData('');

    await authSvc
      .registerUser(FixedUser)
      .then(async (response) => {
        if (response.status === 200 && response.is_active) {
          mixpanelSvc.track('New Register', {
            email: FixedUser.email,
            name: FixedUser.firstName,
            lastName: FixedUser.lastName,
            company: FixedUser.companyName,
            country: FixedUser.countryOrigin,
            userType: FixedUser.userType,
          });

          if (isSubAccount && valueInsert.providerCode) {
            getWhiteLabel(valueInsert.providerCode);
          }

          mixpanelSvc.identify(valueInsert.email);
          mixpanelSvc.track('Successful register');

          const hubspotData = {
            email: (FixedUser.email as string) || '',
            firstName: (FixedUser.firstName as string) || '',
            lastName: (FixedUser.lastName as string) || '',
            phoneNumber: (FixedUser.phone_number as string) || '',
            companyName: (FixedUser.companyName as string) || '',
          };

          await authSvc.registerHubspotUser(hubspotData);

          setShowLoader(false);

          navigate('/login', {
            state: {
              message: t('regSuccessMessage'),
              isSuccessMessage: true,
              email: FixedUser.email,
            },
          });
        } else {
          setErrorMessageData(t('regFailureMessage') as string);
          setShowLoader(false);
        }
      })
      .catch((error) => {
        console.error('Registration error:', error);
        const errorData = error.response?.data;

        if (errorData) {
          const { code, detail, message } = errorData;
          setErrorCode(code ?? undefined);
          setErrorMessageData(
            (message || code) ??
              (detail
                ? detail
                    .split(' ')
                    .join('')
                    .replaceAll('.', '')
                    .replaceAll('"', '')
                : t('regFailureMessage'))
          );
        } else {
          setErrorMessageData(t('regFailureMessage') as string);
        }

        setShowLoader(false);
      });
  };

  if (params.utm_medium === 'email') {
    const dataCampaign = {
      campaign: params.utm_campaign,
      content: params.utm_content,
      email: params.utm_email,
      date: new Date(),
    };
    mixpanelSvc.track('Mail Campaign', dataCampaign);
    if (params.utm_content === 'button_meet') {
      setTimeout(() => {
        window.open('https://calendly.com/efex-team/efex-meetings', '_self');
      }, 500);
    }
  }

  const onSubmit = async (values: any) => {
    await createUser(values);
  };

  useEffect(() => {
    getWhiteLabelFromLocalStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormProvider>
      <Box className="mt-3 mb-7">
        <Box className="flex p-4">
          <Box className="hidden md:block">
            <LogoController
              isWhiteLabel={!!whiteLabelState?.organizationID}
              efexLogoProps={{ className: 'max-w-[150px] ml-4 mt-2' }}
              whiteLabelLogo={whiteLabelState.organizationLogoURL}
            />
          </Box>
          <Box className="w-full flex justify-center items-center md:justify-end space-x-5">
            <Box className="z-50">
              <LangSelector />
            </Box>
            <Button
              appearance="secondary"
              variant="outlined"
              color="primary"
              onClick={() => navigate('/login')}
            >
              {t('regLogin')}
            </Button>
          </Box>
        </Box>

        <Box className="max-w-6xl w-[90%] mx-auto">
          <RegisterForm
            createUser={onSubmit}
            error={errorMessageData}
            errorCode={errorCode}
            loading={showLoader}
            isSubAccount={isSubAccount}
          />
        </Box>
      </Box>
    </FormProvider>
  );
};

export default RegisterLayout;
